import { zodFlavorId } from "../ids.mjs";
import { clinicalInsuranceCategorySchema } from "./clinicalInsuranceCategory.mjs";
import { z as z$1 } from "zod";

// src/models/consultation/bookingFacts.ts
const BOOKING_FACT_TYPES = [
	"booking_step",
	"insurance_category",
	"day_viewed",
	"slot_picked",
	"demographics",
	"consents",
	"policy_created",
	"policy_reused",
	"policy_rechecked",
	"browser_session_changed",
	"service_eligibility"
];
const bookingFactTypeSchema = z$1.enum(BOOKING_FACT_TYPES);
const bookingFactBookingStepCategorySchema = z$1.enum([
	"profile",
	"insurance_on_file",
	"insurance_details",
	"slot",
	"payment"
]);
const bookingFactDemographicsCategorySchema = z$1.enum(["changed", "unchanged"]);
const bookingFactBrowserSessionCategorySchema = z$1.enum([
	"reload",
	"new_session",
	"back_forward"
]);
const bookingFactRefCollectionSchema = z$1.enum([
	"bridge_policies",
	"bridge_providers",
	"bridge_service_eligibilities"
]);
const bookingFactRefSchema = z$1.object({
	id: z$1.string().min(1),
	collection: bookingFactRefCollectionSchema.optional()
}).strict();
const bookingFactBaseSchema = z$1.object({
	id: z$1.string().min(1),
	consultationId: zodFlavorId(),
	customerId: zodFlavorId(),
	factType: bookingFactTypeSchema,
	occurredAt: z$1.unknown(),
	refs: z$1.array(bookingFactRefSchema),
	relatedFactId: z$1.string().min(1).optional(),
	category: z$1.string().min(1).optional(),
	day: z$1.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
	slotStartMs: z$1.number().int().nonnegative().optional(),
	slotDurationMinutes: z$1.number().int().positive().optional(),
	browserSessionId: z$1.string().min(1).optional()
});
const bookingFactSchema = bookingFactBaseSchema.strict().superRefine((fact, ctx) => {
	switch (fact.factType) {
		case "booking_step":
			if (!bookingFactBookingStepCategorySchema.safeParse(fact.category).success) ctx.addIssue({
				code: "custom",
				message: "Invalid booking_step category"
			});
			break;
		case "insurance_category":
			if (!clinicalInsuranceCategorySchema.safeParse(fact.category).success) ctx.addIssue({
				code: "custom",
				message: "Invalid insurance_category category"
			});
			break;
		case "demographics":
			if (!bookingFactDemographicsCategorySchema.safeParse(fact.category).success) ctx.addIssue({
				code: "custom",
				message: "Invalid demographics category"
			});
			break;
		case "browser_session_changed":
			if (!bookingFactBrowserSessionCategorySchema.safeParse(fact.category).success) ctx.addIssue({
				code: "custom",
				message: "Invalid browser_session_changed category"
			});
			if (!fact.browserSessionId) ctx.addIssue({
				code: "custom",
				message: "browserSessionId required"
			});
			break;
		case "day_viewed":
			if (!fact.day) ctx.addIssue({
				code: "custom",
				message: "day required"
			});
			break;
		case "slot_picked": if (fact.slotStartMs == null || fact.slotDurationMinutes == null) ctx.addIssue({
			code: "custom",
			message: "slotStartMs and slotDurationMinutes required"
		});
	}
});
function buildBookingFactDocument(input) {
	const parsed = bookingFactSchema.safeParse({
		...input,
		occurredAt: /* @__PURE__ */ new Date()
	});
	if (!parsed.success) throw parsed.error;
	const { occurredAt: _ignored, ...rest } = parsed.data;
	return rest;
}
function isBookingFactType(value) {
	return BOOKING_FACT_TYPES.includes(value);
}

export { BOOKING_FACT_TYPES, bookingFactBookingStepCategorySchema, bookingFactBrowserSessionCategorySchema, bookingFactDemographicsCategorySchema, bookingFactRefCollectionSchema, bookingFactRefSchema, bookingFactSchema, bookingFactTypeSchema, buildBookingFactDocument, isBookingFactType };
//# sourceMappingURL=bookingFacts.mjs.map